import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Icon from "../../components/Common/Icon";
import { useToast } from "../../context/ToastContext";
import { useTheme } from "../../theme/ThemeContext";
import { THEMES } from "../../theme/themes";
import { useI18n } from "../../i18n/LanguageContext";
import { setTwoFactor } from "../../store/slices/authSlice";

/**
 * Profil / Ayarlar sayfası. Şimdilik tema seçimini barındırır. Temel iki tema
 * (SalonVIP Light / SalonVIP Dark) tüm paketlerde açık; premium üç tema (Rose, Luxe,
 * Botanica) yalnızca Gold pakette. Kilitli temalar soluk ve tıklanamaz gösterilir.
 */
export default function SettingsPage() {
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const { theme, setTheme, available, disabledThemeIds, toggleThemeAvailability } = useTheme();
  const isAdmin = user?.role === "ADMIN";
  const { lang, setLang, languages, t } = useI18n();
  const { showToast } = useToast();

  const isGold = (user?.packageLevel ?? 0) >= 3;
  const twoFaOn = !!user?.twoFactorEnabled;
  const [savingTwoFa, setSavingTwoFa] = useState(false);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  }, []);

  const onToggleTwoFa = async () => {
    if (savingTwoFa) return;
    const next = !twoFaOn;
    setSavingTwoFa(true);
    const result = await dispatch(setTwoFactor(next));
    setSavingTwoFa(false);
    if (setTwoFactor.fulfilled.match(result)) {
      showToast(t(next ? "settings.2fa.enabled" : "settings.2fa.disabled"), "success");
    } else {
      showToast(result.payload || "İşlem başarısız", "error");
    }
  };

  const allowedIds = new Set(available.map((th) => th.id));

  const onSelect = (th) => {
    if (!allowedIds.has(th.id)) return;
    setTheme(th.id);
    showToast(t("settings.theme.applied", { name: th.name }), "success");
  };

  const onSelectLang = (id) => {
    setLang(id);
    showToast(t("settings.lang.applied"), "success");
  };

  return (
    <div className="space-y-6">
      {/* Sayfa başlığı */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
          <Icon name="person" className="!text-[26px]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-on-surface">{t("settings.title")}</h1>
          <p className="text-sm text-on-surface-variant">
            {user?.fullName ?? user?.email} · {user?.role}
          </p>
        </div>
      </div>

      {/* Kategori: Görünüm & Dil */}
      <div className="space-y-4">
        <h3 className="text-xs font-bold text-on-surface-variant/50 uppercase tracking-widest">
          {t("profile.category.appearance")}
        </h3>

        {/* Dil ayarları */}
        <section id="language" className="bg-surface-card rounded-3xl shadow-card border border-outline-variant p-6">
          <h2 className="text-lg font-bold text-on-surface flex items-center gap-2 mb-1">
            <Icon name="language" className="!text-[22px] text-primary" /> {t("settings.lang.title")}
          </h2>
          <p className="text-sm text-on-surface-variant mb-5">{t("settings.lang.desc")}</p>

          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
            {languages.map((l) => {
              const active = lang === l.id;
              return (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => onSelectLang(l.id)}
                  className={`flex items-center gap-3 rounded-2xl border p-3 transition-all ${
                    active
                      ? "border-primary ring-4 ring-primary/15 bg-surface-container-low"
                      : "border-outline-variant hover:border-primary/50 cursor-pointer hover:shadow-card"
                  }`}
                >
                  <span className="text-2xl leading-none">{l.flag}</span>
                  <span className="flex-1 text-left font-semibold text-on-surface">{l.name}</span>
                  {active && (
                    <Icon name="check_circle" className="!text-[18px] filled text-primary" />
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {/* Tema ayarları */}
        <section id="theme" className="bg-surface-card rounded-3xl shadow-card border border-outline-variant p-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-bold text-on-surface flex items-center gap-2">
              <Icon name="palette" className="!text-[22px] text-primary" /> {t("settings.theme.title")}
            </h2>
          </div>
          <p className="text-sm text-on-surface-variant mb-5">{t("settings.theme.desc")}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {THEMES.filter(th => isAdmin || !disabledThemeIds.includes(th.id)).map((th) => {
              const locked = !allowedIds.has(th.id);
              const active = theme === th.id;
              const isPassive = disabledThemeIds.includes(th.id);
              return (
                <div
                  key={th.id}
                  onClick={() => !locked && onSelect(th)}
                  className={`relative text-left rounded-2xl border p-4 transition-all ${
                    active
                      ? "border-primary ring-4 ring-primary/15 bg-surface-container-low"
                      : "border-outline-variant hover:border-primary/50 bg-surface-card"
                  } ${locked ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:shadow-card"}`}
                >
                  {/* Renk önizleme şeridi */}
                  <div className="flex gap-1.5 mb-3">
                    {th.swatch.map((c, i) => (
                      <span
                        key={i}
                        className="h-8 flex-1 rounded-lg border border-black/5"
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-on-surface">{th.name}</span>
                    {active && (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-primary">
                        <Icon name="check_circle" className="!text-[16px] filled" /> {t("settings.theme.active")}
                      </span>
                    )}
                    {locked && (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-tier-gold">
                        <Icon name="lock" className="!text-[15px]" /> Gold
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-on-surface-variant mt-1 leading-snug">
                    {t(`theme.${th.id}.desc`)}
                  </p>

                  {/* Admin Active/Passive Toggle */}
                  {isAdmin && (
                    <div className="mt-4 pt-3 border-t border-outline-variant/30 flex items-center justify-between">
                      <span className="text-[11px] font-semibold text-on-surface-variant">Sistemde Görünürlük</span>
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          const success = await toggleThemeAvailability(th.id);
                          if (success) {
                            showToast("Tema görünürlüğü güncellendi.", "success");
                          } else {
                            showToast("Tema görünürlüğü güncellenemedi.", "error");
                          }
                        }}
                        className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border transition-all ${
                          !isPassive
                            ? "bg-success/10 text-success border-success/20 hover:bg-success/20"
                            : "bg-error/10 text-error border-error/20 hover:bg-error/20"
                        }`}
                      >
                        {!isPassive ? "AKTİF" : "PASİF"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {allowedIds.size < THEMES.length && (
            <div className="mt-5 flex items-start gap-3 bg-tier-gold/10 border border-tier-gold/25 rounded-2xl p-4">
              <Icon name="workspace_premium" className="!text-[22px] text-tier-gold shrink-0" />
              <p className="text-sm text-on-surface-variant">
                <b className="text-on-surface">SalonVIP Rose, Luxe &amp; Botanica</b> {t("settings.theme.premiumNote")}
              </p>
            </div>
          )}
        </section>
      </div>

      {/* Kategori: Güvenlik & Şifre */}
      {isGold && (
        <div className="space-y-4 pt-6 border-t border-outline-variant/30">
          <h3 className="text-xs font-bold text-on-surface-variant/50 uppercase tracking-widest">
            {t("profile.category.security")}
          </h3>

          {/* İki adımlı doğrulama — yalnız Gold */}
          <section id="2fa" className="bg-surface-card rounded-3xl shadow-card border border-outline-variant p-6">
            <h2 className="text-lg font-bold text-on-surface flex items-center gap-2 mb-1">
              <Icon name="verified_user" className="!text-[22px] text-primary" /> {t("settings.2fa.title")}
            </h2>
            <p className="text-sm text-on-surface-variant mb-5">{t("settings.2fa.desc")}</p>

            <div className="flex items-center justify-between gap-4 rounded-2xl border border-outline-variant p-4">
              <div className="flex items-center gap-3">
                <Icon name="sms" className={`!text-[22px] ${twoFaOn ? "text-success" : "text-on-surface-variant"}`} />
                <div>
                  <p className="font-semibold text-on-surface">{t("settings.2fa.toggle")}</p>
                  <p className={`text-xs font-bold ${twoFaOn ? "text-success" : "text-on-surface-variant"}`}>
                    {t(twoFaOn ? "settings.2fa.on" : "settings.2fa.off")}
                  </p>
                </div>
              </div>

              {/* Toggle switch */}
              <button
                type="button"
                role="switch"
                aria-checked={twoFaOn}
                onClick={onToggleTwoFa}
                disabled={savingTwoFa}
                className={`relative w-12 h-7 rounded-full transition-colors shrink-0 disabled:opacity-50 ${
                  twoFaOn ? "bg-primary" : "bg-outline-variant"
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                    twoFaOn ? "translate-x-5" : ""
                  }`}
                />
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
